import { useMemo, useState } from "react";
import {
    ProductBeverageItem,
    TabItem,
} from "@hatch-digital-nl/bravilor-storybook";
import { useStringTranslation } from "../useStringTranslation";
import { LanguageInterface } from "../../interfaces/shared/LanguageInterface";
import { ProductDetailInterface } from "../../interfaces/apiV2/product/ProductDetailInterface";
import {
    buildProductSpecificationEntries,
    ProductSpecificationEntry,
} from "../../utils/productDetail/buildProductSpecificationEntries";
import { mapProductBeveragesForDisplay } from "../../utils/productDetail/mapProductBeveragesForDisplay";
import {
    PRODUCT_SPECIFICATION_TAB,
    ProductSpecificationTabId,
} from "../../utils/productDetail/productSpecificationsConstants";

interface UseProductSpecificationsParams {
    data?: ProductDetailInterface;
    activeLanguage: LanguageInterface | null;
    loading: boolean;
}

interface UseProductSpecificationsReturn {
    showSkeleton: boolean;
    showTabs: boolean;
    hasContent: boolean;
    tabs: TabItem[];
    cardTitle: string;
    specificationsLabel: string;
    activeTabId: ProductSpecificationTabId;
    setActiveTabId: React.Dispatch<
        React.SetStateAction<ProductSpecificationTabId>
    >;
    specificationEntries: ProductSpecificationEntry[];
    beveragesForDisplay: ProductBeverageItem[];
    availableBeverageOptions: string | null;
    isBeveragesTabActive: boolean;
}

export const useProductSpecifications = ({
    data,
    activeLanguage,
    loading,
}: UseProductSpecificationsParams): UseProductSpecificationsReturn => {
    const {
        t,
        translations,
        isLoading: isTranslationsLoading,
        isFetching: isTranslationsFetching,
    } = useStringTranslation();
    const isTranslationsPending =
        isTranslationsLoading || (isTranslationsFetching && !translations);
    const translationsLoaded =
        !!translations && !!activeLanguage && !isTranslationsPending;

    const [activeTabId, setActiveTabId] = useState<ProductSpecificationTabId>(
        PRODUCT_SPECIFICATION_TAB.SPECIFICATIONS,
    );

    const attributes = data?.productAttributes ?? [];
    const beverageCount = data?.beverages?.length ?? 0;
    const hasDimensions = !!data?.dimensions;
    const hasSpecs = hasDimensions || attributes.length > 0;
    const hasBeverages = beverageCount > 0;
    const hasContent = hasSpecs || hasBeverages;
    const showTabs = hasBeverages;
    const showSkeleton = loading || !translationsLoaded;

    const specificationsLabel = t("specifications");
    const beveragesLabel = t("beverages");
    const dimensionsLabel = t("dimensions");

    const tabs: TabItem[] = useMemo(
        () => [
            {
                id: PRODUCT_SPECIFICATION_TAB.SPECIFICATIONS,
                label: specificationsLabel,
            },
            {
                id: PRODUCT_SPECIFICATION_TAB.BEVERAGES,
                label: beveragesLabel,
                count: beverageCount,
            },
        ],
        [specificationsLabel, beveragesLabel, beverageCount],
    );

    const cardTitle =
        activeTabId === PRODUCT_SPECIFICATION_TAB.BEVERAGES
            ? beveragesLabel
            : specificationsLabel;

    const specificationEntries = useMemo(
        () =>
            buildProductSpecificationEntries({
                dimensions: data?.dimensions,
                dimensionsLabel,
                attributes: data?.productAttributes ?? [],
            }),
        [data?.dimensions, data?.productAttributes, dimensionsLabel],
    );

    const beveragesForDisplay = useMemo(
        () => mapProductBeveragesForDisplay(data?.beverages ?? null),
        [data?.beverages],
    );

    const availableBeverageOptions =
        data?.available_beverage_options?.trim() || null;

    const isBeveragesTabActive =
        showTabs && activeTabId === PRODUCT_SPECIFICATION_TAB.BEVERAGES;

    return {
        showSkeleton,
        showTabs,
        hasContent,
        tabs,
        cardTitle,
        specificationsLabel,
        activeTabId,
        setActiveTabId,
        specificationEntries,
        beveragesForDisplay,
        availableBeverageOptions,
        isBeveragesTabActive,
    };
};
